import * as THREE from 'three';
import {
  createGlowMaterial,
  createHaloMaterial,
  createMatteMaterial,
  palette
} from '../../visual';
import {
  ATRIUM_RADIUS,
  CEILING_HEIGHT,
  DOME_HEIGHT,
  FLOOR_Y,
  HANGAR_ARC,
  INTERIOR_RADIUS,
  PATHWAY_INNER,
  PATHWAY_OUTER,
  PATHWAY_WIDTH,
  SHOP_ANGLES,
  polar
} from './StationLayout';

export interface StationInteriorBuildResult {
  readonly root: THREE.Group;
  readonly obstacles: ReadonlyArray<{
    readonly position: THREE.Vector3;
    readonly radius: number;
  }>;
}

const FLOOR_COLOR = 0x141828;
const FLOOR_ACCENT_COLOR = 0x4c6ea8;
const FLOOR_EMISSIVE_COLOR = 0x6ac0ff;
const WALL_COLOR = 0x161b2a;
const WALL_STRIP_COLOR = 0x6ac0ff;
const METAL_TRIM_COLOR = palette.station.hullMid;
const DOME_COLOR = 0x0a0f22;

/**
 * Builds the architecture for the Nexus-style interior. Purely visual geometry;
 * counters, vendors, NPCs and the hangar are built by separate modules.
 */
export function buildStationInterior(): StationInteriorBuildResult {
  const root = new THREE.Group();
  root.name = 'station-interior';
  const obstacles: { position: THREE.Vector3; radius: number }[] = [];

  buildFloor(root);
  buildHexPattern(root);
  buildPathways(root);
  buildAtriumDais(root, obstacles);
  buildCentralEmblem(root);
  buildPerimeterColumns(root, obstacles);
  buildWalls(root);
  buildWindowBand(root);
  buildDomeCeiling(root);
  buildPendantRing(root);
  buildBaseLighting(root);

  return { root, obstacles };
}

// ---------------------------------------------------------------------------
// Floor and hexagonal accent pattern
// ---------------------------------------------------------------------------

function buildFloor(root: THREE.Group): void {
  const floorMat = createMatteMaterial({
    color: FLOOR_COLOR,
    roughness: 0.4
  });
  const floor = new THREE.Mesh(
    new THREE.CircleGeometry(INTERIOR_RADIUS, 96),
    floorMat
  );
  floor.rotation.x = -Math.PI / 2;
  floor.position.y = FLOOR_Y;
  root.add(floor);

  // Darker inlay ring 1u wide near the wall (defines the perimeter).
  const perimeterMat = createMatteMaterial({
    color: 0x0a0f1a,
    roughness: 0.7
  });
  const perimeter = new THREE.Mesh(
    new THREE.RingGeometry(INTERIOR_RADIUS - 2, INTERIOR_RADIUS - 0.2, 96),
    perimeterMat
  );
  perimeter.rotation.x = -Math.PI / 2;
  perimeter.position.y = FLOOR_Y + 0.005;
  root.add(perimeter);
}

function buildHexPattern(root: THREE.Group): void {
  // Emissive hex tiles scattered inside the atrium area.
  const hexMat = createMatteMaterial({
    color: 0x2a3560,
    roughness: 0.45,
    emissive: FLOOR_EMISSIVE_COLOR,
    emissiveIntensity: 0.22
  });

  // Hexagonal grid: row+col offset layout.
  const hexShape = new THREE.Shape();
  const hr = 0.9;
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    const x = Math.cos(a) * hr;
    const z = Math.sin(a) * hr;
    if (i === 0) hexShape.moveTo(x, z);
    else hexShape.lineTo(x, z);
  }
  hexShape.closePath();
  const hexGeo = new THREE.ShapeGeometry(hexShape);
  hexGeo.rotateX(-Math.PI / 2);

  const step = 2.3;
  const hexRowStep = step * Math.sqrt(3) / 2;
  const radiusLimit = ATRIUM_RADIUS - 1.5;
  for (let row = -4; row <= 4; row++) {
    for (let col = -5; col <= 5; col++) {
      const x = col * step + (row % 2 === 0 ? 0 : step / 2);
      const z = row * hexRowStep;
      const r = Math.hypot(x, z);
      if (r > radiusLimit) continue;
      if (r < 1.2) continue; // leave the atrium centre clear for the dais
      if (Math.random() < 0.35) continue; // sparse pattern
      const hex = new THREE.Mesh(hexGeo, hexMat);
      hex.position.set(x, FLOOR_Y + 0.012, z);
      hex.scale.setScalar(0.4 + Math.random() * 0.35);
      root.add(hex);
    }
  }
}

function buildPathways(root: THREE.Group): void {
  const pathMat = createMatteMaterial({
    color: 0x3a5a9a,
    roughness: 0.35,
    emissive: FLOOR_EMISSIVE_COLOR,
    emissiveIntensity: 0.28
  });

  // Radial pathway leading to each shop.
  for (const angle of Object.values(SHOP_ANGLES)) {
    const length = PATHWAY_OUTER - PATHWAY_INNER;
    const mid = (PATHWAY_OUTER + PATHWAY_INNER) / 2;
    const { x, z } = polar(angle, mid);
    const pathGeo = new THREE.PlaneGeometry(PATHWAY_WIDTH, length);
    const path = new THREE.Mesh(pathGeo, pathMat);
    path.rotation.x = -Math.PI / 2;
    path.rotation.z = -angle;
    path.position.set(x, FLOOR_Y + 0.02, z);
    root.add(path);
  }

  // Wider pathway toward the hangar/exit (+Z).
  const hangarPathMat = createMatteMaterial({
    color: 0xf1a95d,
    roughness: 0.35,
    emissive: 0xffd389,
    emissiveIntensity: 0.3
  });
  const hangarPathLength = PATHWAY_OUTER - PATHWAY_INNER;
  const hangarPath = new THREE.Mesh(
    new THREE.PlaneGeometry(PATHWAY_WIDTH * 1.8, hangarPathLength),
    hangarPathMat
  );
  hangarPath.rotation.x = -Math.PI / 2;
  hangarPath.position.set(
    0,
    FLOOR_Y + 0.022,
    (PATHWAY_OUTER + PATHWAY_INNER) / 2
  );
  root.add(hangarPath);

  // Atrium ring marker.
  const ringMat = createMatteMaterial({
    color: FLOOR_ACCENT_COLOR,
    roughness: 0.4,
    emissive: FLOOR_EMISSIVE_COLOR,
    emissiveIntensity: 0.3
  });
  const ring = new THREE.Mesh(
    new THREE.RingGeometry(ATRIUM_RADIUS - 0.18, ATRIUM_RADIUS, 96),
    ringMat
  );
  ring.rotation.x = -Math.PI / 2;
  ring.position.y = FLOOR_Y + 0.015;
  root.add(ring);
}

// ---------------------------------------------------------------------------
// Central atrium dais
// ---------------------------------------------------------------------------

function buildAtriumDais(
  root: THREE.Group,
  obstacles: { position: THREE.Vector3; radius: number }[]
): void {
  const trimMat = createMatteMaterial({
    color: METAL_TRIM_COLOR,
    roughness: 0.55
  });
  const darkMat = createMatteMaterial({
    color: 0x202638,
    roughness: 0.75
  });
  const glow = createGlowMaterial(0x9fd9ff, 0.85, true);

  // Interaction halo on the floor around the dais — signals the
  // central mission terminal.
  const missionGreen = 0xb6ffe6;
  const haloMat = createMatteMaterial({
    color: missionGreen,
    roughness: 0.35,
    emissive: missionGreen,
    emissiveIntensity: 0.4
  });
  const approachHalo = new THREE.Mesh(
    new THREE.RingGeometry(4.5, 5.2, 64),
    haloMat
  );
  approachHalo.rotation.x = -Math.PI / 2;
  approachHalo.position.y = FLOOR_Y + 0.03;
  root.add(approachHalo);

  const approachHaloInner = new THREE.Mesh(
    new THREE.RingGeometry(4.25, 4.4, 64),
    createMatteMaterial({
      color: missionGreen,
      roughness: 0.3,
      emissive: missionGreen,
      emissiveIntensity: 0.55
    })
  );
  approachHaloInner.rotation.x = -Math.PI / 2;
  approachHaloInner.position.y = FLOOR_Y + 0.04;
  root.add(approachHaloInner);

  // Outer dais step (2 stairs total).
  const step1 = new THREE.Mesh(
    new THREE.CylinderGeometry(4.0, 4.2, 0.35, 48),
    darkMat
  );
  step1.position.y = FLOOR_Y + 0.175;
  root.add(step1);

  const step2 = new THREE.Mesh(
    new THREE.CylinderGeometry(3.3, 3.5, 0.4, 48),
    trimMat
  );
  step2.position.y = FLOOR_Y + 0.55;
  root.add(step2);

  // Floating mission terminal hologram beside the main column — hints
  // that the central mast is the missions terminal.
  const missionHaloCore = new THREE.Mesh(
    new THREE.OctahedronGeometry(0.4, 0),
    createGlowMaterial(missionGreen, 0.85, true)
  );
  missionHaloCore.name = 'mission-terminal-holo';
  missionHaloCore.position.set(0, 1.9, 0);
  root.add(missionHaloCore);

  const missionHaloGlow = new THREE.Mesh(
    new THREE.SphereGeometry(0.7, 16, 12),
    createHaloMaterial(missionGreen, 0.2)
  );
  missionHaloGlow.position.set(0, 1.9, 0);
  root.add(missionHaloGlow);

  const stepRing1 = new THREE.Mesh(
    new THREE.TorusGeometry(4.0, 0.04, 8, 56),
    glow
  );
  stepRing1.rotation.x = Math.PI / 2;
  stepRing1.position.y = FLOOR_Y + 0.35;
  root.add(stepRing1);

  const stepRing2 = new THREE.Mesh(
    new THREE.TorusGeometry(3.3, 0.04, 8, 56),
    glow
  );
  stepRing2.rotation.x = Math.PI / 2;
  stepRing2.position.y = FLOOR_Y + 0.75;
  root.add(stepRing2);

  // Central support column for the floating emblem.
  const column = new THREE.Mesh(
    new THREE.CylinderGeometry(0.35, 0.55, 5.5, 16),
    trimMat
  );
  column.position.y = FLOOR_Y + 3.5;
  root.add(column);

  obstacles.push({
    position: new THREE.Vector3(0, 0, 0),
    radius: 4.2
  });
}

function buildCentralEmblem(root: THREE.Group): void {
  const ringBlue = createGlowMaterial(0x9fd9ff, 0.8, true);
  const ringGold = createGlowMaterial(0xffd389, 0.75, true);
  const ringWhite = createGlowMaterial(0xffffff, 0.95, true);

  const outer = new THREE.Mesh(
    new THREE.TorusGeometry(2.6, 0.16, 12, 48),
    ringBlue
  );
  outer.name = 'station-emblem';
  outer.position.y = 8.5;
  outer.rotation.x = Math.PI / 4;
  root.add(outer);

  const middle = new THREE.Mesh(
    new THREE.TorusGeometry(1.9, 0.12, 10, 40),
    ringGold
  );
  middle.name = 'station-emblem-inner';
  middle.position.y = 8.5;
  middle.rotation.x = -Math.PI / 5;
  root.add(middle);

  const inner = new THREE.Mesh(
    new THREE.TorusGeometry(1.2, 0.08, 10, 32),
    ringWhite
  );
  inner.name = 'station-emblem-thin';
  inner.position.y = 8.5;
  inner.rotation.x = Math.PI / 3;
  root.add(inner);

  const core = new THREE.Mesh(
    new THREE.IcosahedronGeometry(0.55, 1),
    ringWhite
  );
  core.position.y = 8.5;
  root.add(core);

  const coreHalo = new THREE.Mesh(
    new THREE.SphereGeometry(1.1, 16, 12),
    createHaloMaterial(0x9fd9ff, 0.16)
  );
  coreHalo.position.y = 8.5;
  root.add(coreHalo);
}

// ---------------------------------------------------------------------------
// Perimeter columns — decorative pillars framing the alcoves
// ---------------------------------------------------------------------------

function buildPerimeterColumns(
  root: THREE.Group,
  obstacles: { position: THREE.Vector3; radius: number }[]
): void {
  const hullMat = createMatteMaterial({
    color: palette.station.hull,
    roughness: 0.45
  });
  const darkMat = createMatteMaterial({
    color: 0x13192a,
    roughness: 0.7
  });
  const glow = createGlowMaterial(0x9fd9ff, 0.85, true);

  // 12 columns at regular angles, excluding the ones blocking the alcove
  // openings or the hangar arc. Simpler: place 8 columns between the 4 shop
  // alcoves and the hangar opening.
  const columnAngles = [
    // Between alcoves and hangar axis.
    Math.PI * 0.18,
    Math.PI * 0.48,
    Math.PI * 0.85,
    Math.PI * 1.15,
    Math.PI * 1.52,
    Math.PI * 1.82
  ];

  const columnRadius = INTERIOR_RADIUS - 1.2;
  for (const angle of columnAngles) {
    const { x, z } = polar(angle, columnRadius);

    // Column body.
    const column = new THREE.Mesh(
      new THREE.BoxGeometry(1.2, CEILING_HEIGHT - 2.5, 1.2),
      hullMat
    );
    column.position.set(x, (CEILING_HEIGHT - 2.5) / 2 + FLOOR_Y, z);
    column.lookAt(0, column.position.y, 0);
    root.add(column);

    // Base block.
    const base = new THREE.Mesh(
      new THREE.BoxGeometry(1.6, 0.6, 1.6),
      darkMat
    );
    base.position.set(x, FLOOR_Y + 0.3, z);
    root.add(base);

    // Vertical emissive strip on inward face.
    const inward = new THREE.Vector3(-x, 0, -z).normalize();
    const strip = new THREE.Mesh(
      new THREE.BoxGeometry(0.28, CEILING_HEIGHT - 6, 0.08),
      glow
    );
    strip.position.set(
      x + inward.x * 0.65,
      (CEILING_HEIGHT - 6) / 2 + FLOOR_Y + 1,
      z + inward.z * 0.65
    );
    strip.lookAt(0, strip.position.y, 0);
    root.add(strip);

    // Capital on top.
    const capital = new THREE.Mesh(
      new THREE.BoxGeometry(1.8, 0.4, 1.8),
      darkMat
    );
    capital.position.set(x, CEILING_HEIGHT - 2.3 + FLOOR_Y, z);
    root.add(capital);

    obstacles.push({
      position: new THREE.Vector3(x, 0, z),
      radius: 1.0
    });
  }
}

// ---------------------------------------------------------------------------
// Walls
// ---------------------------------------------------------------------------

function buildWalls(root: THREE.Group): void {
  const wallMat = createMatteMaterial({
    color: WALL_COLOR,
    roughness: 0.85
  });
  const wall = new THREE.Mesh(
    new THREE.CylinderGeometry(
      INTERIOR_RADIUS + 0.4,
      INTERIOR_RADIUS + 0.4,
      CEILING_HEIGHT,
      128,
      1,
      true
    ),
    wallMat
  );
  wall.material.side = THREE.BackSide;
  wall.position.y = CEILING_HEIGHT / 2 + FLOOR_Y;
  root.add(wall);

  // Emissive skirting at the bottom.
  const skirtingMat = createMatteMaterial({
    color: WALL_STRIP_COLOR,
    roughness: 0.35,
    emissive: WALL_STRIP_COLOR,
    emissiveIntensity: 0.45
  });
  const skirting = new THREE.Mesh(
    new THREE.CylinderGeometry(
      INTERIOR_RADIUS + 0.2,
      INTERIOR_RADIUS + 0.2,
      0.24,
      128,
      1,
      true
    ),
    skirtingMat
  );
  skirting.material.side = THREE.BackSide;
  skirting.position.y = FLOOR_Y + 0.28;
  root.add(skirting);

  // Wall-top trim ring.
  const trimMat = createMatteMaterial({
    color: METAL_TRIM_COLOR,
    roughness: 0.55
  });
  const trim = new THREE.Mesh(
    new THREE.CylinderGeometry(
      INTERIOR_RADIUS + 0.45,
      INTERIOR_RADIUS + 0.45,
      0.6,
      128,
      1,
      true
    ),
    trimMat
  );
  trim.material.side = THREE.BackSide;
  trim.position.y = CEILING_HEIGHT - 1 + FLOOR_Y;
  root.add(trim);

  // Thin emissive band just beneath the trim.
  const trimGlow = new THREE.Mesh(
    new THREE.CylinderGeometry(
      INTERIOR_RADIUS + 0.25,
      INTERIOR_RADIUS + 0.25,
      0.08,
      128,
      1,
      true
    ),
    skirtingMat
  );
  trimGlow.material.side = THREE.BackSide;
  trimGlow.position.y = CEILING_HEIGHT - 1.65 + FLOOR_Y;
  root.add(trimGlow);
}

// ---------------------------------------------------------------------------
// Window band — continuous panoramic strip at mid height (hinting at space outside)
// ---------------------------------------------------------------------------

function buildWindowBand(root: THREE.Group): void {
  const windowMat = createGlowMaterial(0x0c1530, 0.9, true);
  windowMat.side = THREE.BackSide;
  const band = new THREE.Mesh(
    new THREE.CylinderGeometry(
      INTERIOR_RADIUS + 0.2,
      INTERIOR_RADIUS + 0.2,
      4.2,
      128,
      1,
      true
    ),
    windowMat
  );
  band.position.y = CEILING_HEIGHT * 0.55 + FLOOR_Y;
  root.add(band);

  // Subtle starfield glow on the window (emissive small dots via disks).
  const starMat = createGlowMaterial(0xb4e3ff, 0.9, true);
  const starGeo = new THREE.CircleGeometry(0.12, 8);
  const starCount = 80;
  for (let i = 0; i < starCount; i++) {
    const angle = Math.random() * Math.PI * 2;
    const yRange = CEILING_HEIGHT * 0.55 + FLOOR_Y;
    const y = yRange + (Math.random() - 0.5) * 3.6;
    const star = new THREE.Mesh(starGeo, starMat);
    const r = INTERIOR_RADIUS + 0.25;
    const x = Math.cos(angle) * r;
    const z = Math.sin(angle) * r;
    star.position.set(x, y, z);
    star.lookAt(0, y, 0);
    star.scale.setScalar(0.3 + Math.random() * 0.7);
    root.add(star);
  }

  // Window mullions (vertical dividers).
  const mullionMat = createMatteMaterial({
    color: 0x1d2438,
    roughness: 0.75
  });
  const mullionGeo = new THREE.BoxGeometry(0.22, 4.6, 0.2);
  for (let i = 0; i < 32; i++) {
    const angle = (i / 32) * Math.PI * 2;
    const r = INTERIOR_RADIUS + 0.32;
    const m = new THREE.Mesh(mullionGeo, mullionMat);
    m.position.set(Math.cos(angle) * r, CEILING_HEIGHT * 0.55 + FLOOR_Y, Math.sin(angle) * r);
    m.lookAt(0, m.position.y, 0);
    root.add(m);
  }

  // Top & bottom horizontal rails of the window band.
  const railMat = createMatteMaterial({
    color: METAL_TRIM_COLOR,
    roughness: 0.55
  });
  const topRail = new THREE.Mesh(
    new THREE.CylinderGeometry(
      INTERIOR_RADIUS + 0.35,
      INTERIOR_RADIUS + 0.35,
      0.28,
      128,
      1,
      true
    ),
    railMat
  );
  topRail.material.side = THREE.BackSide;
  topRail.position.y = CEILING_HEIGHT * 0.55 + 2.25 + FLOOR_Y;
  root.add(topRail);

  const botRail = topRail.clone();
  botRail.position.y = CEILING_HEIGHT * 0.55 - 2.25 + FLOOR_Y;
  root.add(botRail);

  // Silence unused import warning.
  void HANGAR_ARC;
}

// ---------------------------------------------------------------------------
// Dome ceiling
// ---------------------------------------------------------------------------

function buildDomeCeiling(root: THREE.Group): void {
  const domeMat = createMatteMaterial({
    color: DOME_COLOR,
    roughness: 0.85
  });
  domeMat.side = THREE.BackSide;
  const dome = new THREE.Mesh(
    new THREE.SphereGeometry(
      INTERIOR_RADIUS + 0.6,
      96,
      40,
      0,
      Math.PI * 2,
      0,
      Math.PI / 2
    ),
    domeMat
  );
  dome.position.y = CEILING_HEIGHT + FLOOR_Y;
  dome.scale.y = DOME_HEIGHT / (INTERIOR_RADIUS + 0.6);
  root.add(dome);

  // Dome ribs (thicker, 12 visible lines).
  const ribCount = 12;
  for (let i = 0; i < ribCount; i++) {
    const angle = (i / ribCount) * Math.PI * 2;
    const curve = new THREE.EllipseCurve(
      0,
      0,
      INTERIOR_RADIUS + 0.45,
      DOME_HEIGHT,
      -Math.PI / 2,
      Math.PI / 2,
      false,
      0
    );
    const pts = curve
      .getPoints(32)
      .map((p) => new THREE.Vector3(p.x, p.y + CEILING_HEIGHT + FLOOR_Y, 0));
    const geo = new THREE.BufferGeometry().setFromPoints(pts);
    const line = new THREE.Line(
      geo,
      new THREE.LineBasicMaterial({
        color: 0x5a82c4,
        transparent: true,
        opacity: 0.55
      })
    );
    line.rotation.y = angle;
    root.add(line);
  }

  // Inner halo sphere for depth.
  const haloSphere = new THREE.Mesh(
    new THREE.SphereGeometry(
      INTERIOR_RADIUS - 1,
      32,
      16,
      0,
      Math.PI * 2,
      0,
      Math.PI / 2
    ),
    createHaloMaterial(0x6ac0ff, 0.05)
  );
  haloSphere.position.y = CEILING_HEIGHT + FLOOR_Y;
  haloSphere.scale.y = DOME_HEIGHT / (INTERIOR_RADIUS - 1);
  root.add(haloSphere);

  // Central skylight — large glowing disc at the dome apex.
  const skylight = new THREE.Mesh(
    new THREE.CircleGeometry(6, 64),
    createGlowMaterial(0xffe6b0, 0.6, true)
  );
  skylight.position.y = CEILING_HEIGHT + DOME_HEIGHT - 0.4 + FLOOR_Y;
  skylight.rotation.x = -Math.PI / 2;
  root.add(skylight);

  // Skylight frame.
  const frameMat = createMatteMaterial({
    color: 0x2a2f42,
    roughness: 0.6
  });
  const frame = new THREE.Mesh(
    new THREE.TorusGeometry(6.2, 0.25, 12, 56),
    frameMat
  );
  frame.position.y = CEILING_HEIGHT + DOME_HEIGHT - 0.25 + FLOOR_Y;
  frame.rotation.x = Math.PI / 2;
  root.add(frame);

  // Radiating beams from the skylight (thin triangles/cones downward).
  const beamMat = createHaloMaterial(0xfff1d6, 0.045);
  for (let i = 0; i < 6; i++) {
    const angle = (i / 6) * Math.PI * 2;
    const beam = new THREE.Mesh(
      new THREE.ConeGeometry(1.5, DOME_HEIGHT + 2, 8, 1, true),
      beamMat
    );
    beam.position.set(
      Math.cos(angle) * 2.5,
      CEILING_HEIGHT + DOME_HEIGHT / 2 + FLOOR_Y,
      Math.sin(angle) * 2.5
    );
    root.add(beam);
  }
}

// ---------------------------------------------------------------------------
// Pendant ring — lamp necklace hanging from the dome base
// ---------------------------------------------------------------------------

function buildPendantRing(root: THREE.Group): void {
  const chainMat = createMatteMaterial({
    color: 0x1d2232,
    roughness: 0.75
  });
  const lampMat = createGlowMaterial(0xffd389, 0.95, true);
  const lampShellMat = createMatteMaterial({
    color: 0x2a2f42,
    roughness: 0.55
  });

  const count = 10;
  const ringRadius = ATRIUM_RADIUS + 4;
  for (let i = 0; i < count; i++) {
    const angle = (i / count) * Math.PI * 2 + Math.PI / count;
    const x = Math.cos(angle) * ringRadius;
    const z = Math.sin(angle) * ringRadius;

    const chain = new THREE.Mesh(
      new THREE.CylinderGeometry(0.045, 0.045, 3.0, 6),
      chainMat
    );
    chain.position.set(x, CEILING_HEIGHT - 1.8 + FLOOR_Y, z);
    root.add(chain);

    const shell = new THREE.Mesh(
      new THREE.SphereGeometry(0.5, 16, 12),
      lampShellMat
    );
    shell.position.set(x, CEILING_HEIGHT - 3.4 + FLOOR_Y, z);
    root.add(shell);

    const lamp = new THREE.Mesh(
      new THREE.SphereGeometry(0.38, 14, 10),
      lampMat
    );
    lamp.position.set(x, CEILING_HEIGHT - 3.4 + FLOOR_Y, z);
    root.add(lamp);
  }
}

// ---------------------------------------------------------------------------
// Upwards-facing floor uplights for drama
// ---------------------------------------------------------------------------

function buildBaseLighting(root: THREE.Group): void {
  const glowMat = createGlowMaterial(0x9fd9ff, 0.55, true);
  const haloMat = createHaloMaterial(0x9fd9ff, 0.06);
  const count = 16;
  const ringR = INTERIOR_RADIUS * 0.78;
  for (let i = 0; i < count; i++) {
    const angle = (i / count) * Math.PI * 2;
    const x = Math.cos(angle) * ringR;
    const z = Math.sin(angle) * ringR;
    const disk = new THREE.Mesh(
      new THREE.CircleGeometry(0.55, 18),
      glowMat
    );
    disk.rotation.x = -Math.PI / 2;
    disk.position.set(x, FLOOR_Y + 0.025, z);
    root.add(disk);

    const halo = new THREE.Mesh(
      new THREE.ConeGeometry(0.6, 4.2, 12, 1, true),
      haloMat
    );
    halo.position.set(x, FLOOR_Y + 2.1, z);
    root.add(halo);
  }
}
